/* eslint-disable @typescript-eslint/no-explicit-any */
import defineProperty from '../defineProperty'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import utils from './utils'
import type { ApiGatewayRequest } from '../../awsApiGatewayClient'

function ownKeys(object: object, enumerableOnly: boolean) {
  const keys = Object.keys(object)
  if (Object.getOwnPropertySymbols) {
    let symbols = Object.getOwnPropertySymbols(object)
    if (enumerableOnly) {
      // @ts-expect-error JavaScript由来なので
      symbols = symbols.filter(sym => Object.getOwnPropertyDescriptor(object, sym).enumerable)
    }
    // @ts-expect-error JavaScript由来なので
    keys.push(...symbols)
  }
  return keys
}

// @ts-expect-error JavaScript由来なので
function _objectSpread(target, ...sources) {
  sources.forEach((source, i) => {
    source = source != null ? source : {}
    if (i % 2) {
      ownKeys(source, true).forEach(key => {
        defineProperty(target, key, source[key])
      })
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source))
    } else {
      // @ts-expect-error JavaScript由来なので
      ownKeys(source).forEach(key => {
        // @ts-expect-error JavaScript由来なので
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key))
      })
    }
  })
  return target
}

interface KeyValues {
  [key: string]: string
}

export interface SimpleHttpClient {
  endpoint: string
  makeRequest: (request: ApiGatewayRequest) => any
}

export interface SimpleHttpClientConfig {
  accessKey: string | undefined
  secretKey: string | undefined
  sessionToken: string | undefined
  serviceName: string | undefined
  region: string | undefined
  endpoint: string | undefined
  retries: number | undefined
  retryCondition: string | undefined
  retryDelay: string | number | (() => number) | undefined
  host: string | undefined
  defaultContentType: string | undefined
  defaultAcceptType: string | undefined
  systemClockOffset: number | undefined
  headers: KeyValues | undefined
}

export interface SimpleHttpClientFactory {
  newClient: (config: SimpleHttpClientConfig) => SimpleHttpClient
}

// @ts-expect-error we define newClient soon later
export const simpleHttpClientFactory: SimpleHttpClientFactory = {}

simpleHttpClientFactory.newClient = function (config) {
  function buildCanonicalQueryString(queryParams: KeyValues) {
    // Build a properly encoded query string from a QueryParam object
    if (Object.keys(queryParams).length < 1) {
      return ''
    }

    let canonicalQueryString = ''

    for (const property in queryParams) {
      if (Object.prototype.hasOwnProperty.call(queryParams, property)) {
        canonicalQueryString +=
          encodeURIComponent(property) + '=' + encodeURIComponent(queryParams[property]) + '&'
      }
    }

    return canonicalQueryString.substr(0, canonicalQueryString.length - 1)
  }

  // @ts-expect-error we define properties soon later
  const simpleHttpClient: SimpleHttpClient = {}

  simpleHttpClient.endpoint = utils.assertDefined(config.endpoint, 'endpoint')

  simpleHttpClient.makeRequest = function (request: ApiGatewayRequest) {
    const verb = utils.assertDefined(request.verb, 'verb')
    const path = utils.assertDefined(request.path, 'path')
    let queryParams = utils.copy(request.queryParams)
    let timeout = utils.copy(request.timeout)

    if (queryParams === undefined) {
      queryParams = {}
    }

    if (timeout === undefined) {
      timeout = 0
    }

    const headers = _objectSpread(_objectSpread({}, utils.copy(request.headers)), config.headers) // If the user has not specified an override for Content type the use default

    if (headers['Content-Type'] === undefined) {
      headers['Content-Type'] = config.defaultContentType
    } // If the user has not specified an override for Accept type the use default

    if (headers['Accept'] === undefined) {
      headers['Accept'] = config.defaultAcceptType
    }

    const body = utils.copy(request.body)

    let url = config.endpoint + path
    const queryString = buildCanonicalQueryString(queryParams)

    if (queryString !== '') {
      url += '?' + queryString
    }

    const simpleHttpRequest = {
      headers: headers,
      timeout: timeout,
      data: body,
      method: verb,
      url: url,
      baseURL: undefined
    }

    if (config.retries !== undefined) {
      simpleHttpRequest.baseURL = url

      const client = axios.create(simpleHttpRequest) // Allow user configurable delay, or built-in exponential delay

      let retryDelay = function retryDelay() {
        return 0
      }

      if (config.retryDelay === 'exponential') {
        retryDelay = axiosRetry.exponentialDelay
      } else if (typeof config.retryDelay === 'string') {
        retryDelay = function retryDelay() {
          return parseInt(config.retryDelay as string)
        }
      } else if (typeof config.retryDelay === 'number') {
        retryDelay = function retryDelay() {
          return config.retryDelay as number
        }
      } else if (typeof config.retryDelay === 'function') {
        retryDelay = config.retryDelay
      }

      axiosRetry(
        client,
        _objectSpread(
          _objectSpread({}, config),
          {},
          {
            retryCondition:
              typeof config.retryCondition === 'function'
                ? config.retryCondition
                : axiosRetry.isNetworkOrIdempotentRequestError,
            retryDelay: retryDelay
          }
        )
      )
      return client.request(simpleHttpRequest)
    }

    return axios(simpleHttpRequest)
  }

  return simpleHttpClient
}

export default simpleHttpClientFactory
